/**
 * Google Ads + GA4 + Meta Pixel Conversion Event Tracking
 *
 * Architecture:
 * - All events are consent-gated: only fire after user clicks "Accept All"
 * - Google Ads conversion labels are configurable via VITE_ env vars
 * - Falls back gracefully when scripts haven't loaded yet (queues via dataLayer)
 *
 * Google Ads Conversion ID: AW-738329296
 * Conversion labels are set in .env as:
 *   VITE_GADS_CONTACT_LABEL   — contact form submit
 *   VITE_GADS_CTA_LABEL       — "Start Sending" / primary CTA clicks
 *   VITE_GADS_PHONE_LABEL     — phone number clicks
 *   VITE_GADS_WHATSAPP_LABEL  — WhatsApp button clicks
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const GADS_ID = "AW-738329296";

// Conversion labels — set via env vars, fall back to placeholder if not configured
const LABELS = {
  contact: import.meta.env.VITE_GADS_CONTACT_LABEL || "",
  cta: import.meta.env.VITE_GADS_CTA_LABEL || "",
  phone: import.meta.env.VITE_GADS_PHONE_LABEL || "",
  whatsapp: import.meta.env.VITE_GADS_WHATSAPP_LABEL || "",
};

// ─── Consent check ────────────────────────────────────────────────────────────
// Matches the key written by CookieConsent.tsx ("bharat_sms_cookie_consent")
function hasConsent(): boolean {
  try {
    return localStorage.getItem("bharat_sms_cookie_consent") === "accepted";
  } catch {
    return false;
  }
}

// ─── Core helpers ─────────────────────────────────────────────────────────────

/** Fire a GA4 / Google Ads gtag event */
function gtag(command: string, ...args: unknown[]) {
  if (!hasConsent()) return;
  if (typeof window.gtag === "function") {
    window.gtag(command, ...args);
  } else {
    // gtag not yet loaded — push to dataLayer so GTM picks it up
    window.dataLayer = window.dataLayer || [];
    (window.dataLayer as unknown[]).push([command, ...args]);
  }
}

/** Fire a Google Ads conversion event */
function gadsConversion(label: string, value = 1) {
  if (!label) return; // label not configured — skip silently
  gtag("event", "conversion", {
    send_to: `${GADS_ID}/${label}`,
    value,
    currency: "INR",
  });
}

/** Fire a Meta Pixel event */
function fbq(eventName: string, params?: Record<string, unknown>) {
  if (!hasConsent()) return;
  if (typeof window.fbq === "function") {
    window.fbq("track", eventName, params);
  }
}

// ─── Public tracking functions ────────────────────────────────────────────────

/**
 * Track contact form submission.
 * Call this in the form's onSubmit handler after validation passes.
 */
export function trackFormSubmission(formName: string, extra?: Record<string, string>) {
  // GA4 — generate_lead is the recommended event for lead forms
  gtag("event", "generate_lead", {
    event_category: "form",
    event_label: formName,
    value: 1,
  });

  // Google Ads conversion
  gadsConversion(LABELS.contact, 1);

  // Meta Pixel
  fbq("Lead", { content_name: formName, content_category: "form_submission", ...extra });
}

/**
 * Track primary CTA button clicks ("Start Sending", "Get Free Trial", etc.)
 * Call this in the onClick handler of the main CTAs.
 */
export function trackCtaClick(ctaName: string, pageName: string) {
  gtag("event", "cta_click", {
    event_category: "engagement",
    event_label: ctaName,
    page: pageName,
  });

  // Google Ads conversion — high-intent CTA
  gadsConversion(LABELS.cta, 1);

  fbq("InitiateCheckout", { content_name: ctaName, content_category: "cta_click" });
}

/**
 * Track phone number click (tel: links).
 * Call this in the onClick of any phone anchor tag.
 */
export function trackPhoneClick(phoneNumber: string) {
  gtag("event", "click_to_call", {
    event_category: "engagement",
    event_label: phoneNumber,
    value: 1,
  });

  gadsConversion(LABELS.phone, 1);

  fbq("Contact", { content_name: "Phone Call", content_category: "click_to_call" });
}

/**
 * Track WhatsApp button click.
 * Call this in the onClick of WhatsApp CTAs.
 */
export function trackWhatsAppClick(source = "floating_button") {
  gtag("event", "whatsapp_click", {
    event_category: "engagement",
    event_label: source,
    value: 1,
  });

  gadsConversion(LABELS.whatsapp, 1);

  fbq("Contact", { content_name: "WhatsApp", content_category: "whatsapp_click" });
}

/**
 * Track service page view — used for remarketing audiences.
 * Call this in a useEffect on service/product pages.
 */
export function trackServiceView(serviceName: string) {
  gtag("event", "view_item", {
    event_category: "service",
    event_label: serviceName,
  });

  fbq("ViewContent", { content_name: serviceName, content_type: "service" });
}

/**
 * Track pricing page view — high-intent remarketing signal.
 */
export function trackPricingView() {
  gtag("event", "view_pricing", {
    event_category: "engagement",
    event_label: "pricing_page",
  });

  fbq("ViewContent", { content_name: "Pricing", content_type: "pricing" });
}

/**
 * Track blog/resource page view — used for content remarketing.
 */
/**
 * Track a launch from the Tools hub.
 * The stable tool name and path make GA4 and GTM reports easy to group.
 */
export function trackToolLaunch(toolName: string, toolPath: string) {
  gtag("event", "tool_launch", {
    event_category: "tool",
    event_label: toolName,
    tool_name: toolName,
    tool_path: toolPath,
    page: typeof window !== "undefined" ? window.location.pathname : "/tools",
  });

  pushDataLayerEvent("tool_launch", {
    tool_name: toolName,
    tool_path: toolPath,
  });

  fbq("ViewContent", { content_name: toolName, content_type: "tool" });
}

export function trackContentView(contentTitle: string, contentType = "blog") {
  gtag("event", "view_content", {
    event_category: "content",
    event_label: contentTitle,
    content_type: contentType,
  });

  fbq("ViewContent", { content_name: contentTitle, content_type: contentType });
}

/**
 * Push a custom event to the GTM dataLayer.
 * Use for any custom GTM trigger that isn't covered above.
 */
export function pushDataLayerEvent(eventName: string, params?: Record<string, unknown>) {
  if (!hasConsent()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName, ...params });
}
